import { cn } from "@/ds/utils";
import { GalleryInfiniteScroller } from "@/features/common/components/dataview/GalleryInfiniteScroller";
import type { DataViewBaseProps, DataViewComponentTypeProps, DataViewDataProps } from "@/features/common/components/dataview/DataViewTypes";
import { useDataItems } from "@/features/common/components/dataview/DataViewTypes";
import type { ComponentType, FC, PropsWithChildren } from "react";
import React from "react";

export type DataViewGalleryProps<T, U = undefined> = {
	galleryComponent: ComponentType<DataViewComponentTypeProps<T, U>>;
	galleryMinWidth?: number;
};

export const DataViewGallery = <T, U = undefined>(props: DataViewBaseProps<T, U> & DataViewDataProps<T> & DataViewGalleryProps<T, U>): React.JSX.Element => {
	const {
		galleryComponent: GalleryComponent,
		galleryMinWidth: minWidth,
		getKey,
		additionalProps,
		scrollRef
	} = props;

	const forQuery = "forQuery" in props ? props.forQuery : undefined;
	const cards = useDataItems(props);

	const className = minWidth === undefined
		? "grid-cols-1 @sm:grid-cols-1 @md:grid-cols-2 @lg:grid-cols-3 @xl:grid-cols-4 @2xl:grid-cols-5"
		: undefined;

	const style = minWidth !== undefined
		? { gridTemplateColumns: `repeat(auto-fill, minmax(${minWidth}px, 1fr))` }
		: undefined;

	return (
		<div className={cn("grid gap-3 p-3", className)} style={style}>
			{
				cards.map(x => {
					const componentProps = {
						...x,
						...additionalProps
					} as DataViewComponentTypeProps<T, U>;

					return <GalleryComponent key={getKey(x)} {...componentProps} />;
				})
			}

			{
				forQuery &&
				<GalleryInfiniteScroller forQuery={forQuery} scrollRef={scrollRef} />
			}
		</div>
	);
};

export interface GalleryCardProps extends PropsWithChildren {
	onClick?: () => void;
}

export const GalleryCard: FC<GalleryCardProps> = ({ children, onClick }) => {
	return (
		<div className={cn("border rounded overflow-hidden", onClick && "cursor-pointer")} onClick={onClick}>
			{children}
		</div>
	);
};
